import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./TeacherSearchBar.module.sass";
import TextInput from "../TextInput";
import LetterAvatar from "../LetterAvatar";
import Icon from "../Icon";
import { searchTeachers } from "../../services/user.service";

const TeacherSearchBar = ({ 
  className,
  label = "Rechercher un formateur",
  placeholder = "Nom ou prénom (min. 2 caractères)",
  onSelectTeacher,
  showDetails = true,
  required = false,
  selectedTeacher: initialSelectedTeacher = null
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(initialSelectedTeacher);
  const [showResults, setShowResults] = useState(false);

  // Initialiser avec le professeur sélectionné
  useEffect(() => {
    if (initialSelectedTeacher) {
      setSelectedTeacher(initialSelectedTeacher);
      setSearchQuery(`${initialSelectedTeacher.first_name} ${initialSelectedTeacher.last_name}`);
    }
  }, [initialSelectedTeacher]);

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    const fetchTeachers = async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchTeachers(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des formateurs:', error);
          setSearchResults([]);
          setShowResults(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };
    
    fetchTeachers();
  }, [searchQuery]);

  // Gérer la sélection d'un formateur
  const handleSelectTeacher = (teacher) => {
    setSelectedTeacher(teacher);
    setSearchQuery(`${teacher.first_name} ${teacher.last_name}`);
    setShowResults(false);
    
    // Transmettre le formateur sélectionné au composant parent
    if (onSelectTeacher) {
      onSelectTeacher(teacher);
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (selectedTeacher && e.target.value !== `${selectedTeacher.first_name} ${selectedTeacher.last_name}`) {
      setSelectedTeacher(null);
      if (onSelectTeacher) {
        onSelectTeacher(null);
      }
    }
  };

  // Réinitialiser la recherche
  const handleReset = () => {
    setSelectedTeacher(null);
    setSearchQuery('');
    if (onSelectTeacher) {
      onSelectTeacher(null);
    }
  };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <TextInput
            className={styles.field}
            label={label}
            name="teacher-search"
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            required={required}
          />
          {searchQuery && (
            <button className={styles.clearButton} onClick={handleReset} type="button" aria-label="Effacer la recherche">
              <Icon name="close-circle" size="20" />
            </button>
          )}
        </div>
        
        {showResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map(teacher => (
              <div 
                key={teacher.id} 
                className={styles.resultItem}
                onClick={() => handleSelectTeacher(teacher)}
              >
                <div className={styles.resultContent}>
                  <div style={{ marginRight: '20px' }}>
                    <LetterAvatar
                      firstName={teacher.first_name}
                      lastName={teacher.last_name}
                      size={40}
                    />
                  </div>
                  <div className={styles.teacherInfo}>
                    <div className={styles.teacherName}>{teacher.first_name} {teacher.last_name}</div>
                    <div className={styles.teacherDetails}>
                      <span>{teacher.email || 'Email non disponible'}</span>
                      {teacher.phone && (
                        <>
                          <span>•</span>
                          <span>{teacher.phone}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !selectedTeacher && (
          <div className={styles.noResults}>
            <div className={styles.noResultsContent}>
              <Icon name="search" size="24" />
              <span>Aucun formateur trouvé</span>
            </div>
          </div>
        )}
      </div>
      
      {selectedTeacher && showDetails && (
        <div className={styles.selectedTeacherDetails}>
          <h3>Détails du formateur sélectionné</h3>
          <div className={styles.detailRow}>
            <span className={styles.label}>Nom:</span>
            <span className={styles.value}>{selectedTeacher.first_name} {selectedTeacher.last_name}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Email:</span>
            <span className={styles.value}>{selectedTeacher.email || 'Non renseigné'}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSearchBar;
